import { inject, Injectable } from '@angular/core';
import { PostgrestError } from '@supabase/supabase-js';
import { from, Observable } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import { AuthService } from '../../../core/auth/auth.service';
import { SupabaseService } from '../../../core/supabase.service';
import { Tables } from '../../../models/enums';
import { Profile, RoomAttendeeResponse, RoomAttendeeRole, RoomReservation, RoomReservationAttendee } from '../../../models/interfaces';
import { currentMembersOnly, flattenProfileMemberships, PROFILE_MEMBERSHIP_SELECT_LIGHT } from '../../../models/profile-membership';
import { AttendeeDraft } from '../utils/room-attendees';

/** Membre invitable (sélecteur de participants). */
export interface RoomMemberOption {
  id: string;
  first_name: string | null;
  last_name: string | null;
  email: string | null;
  photo_url: string | null;
}

/**
 * LECTURE par la vue `room_reservations_visible` (MOKA-242) : la table
 * `room_reservations` ne laisse plus lire `title`, `description`,
 * `video_link` (privilèges de colonne) ; la vue les renvoie masqués
 * (« Réunion privée ») aux non-participants d'une réunion privée.
 * ÉCRITURE sur la table, avec `.select('id')` puis relecture par la vue.
 *
 * - Salle : FK COMPOSITE (room_id, organisation_id) → l'embed nomme la contrainte.
 * - Profil : colonnes explicites, jamais `*`.
 * - Participants membres : FK composite rra_member_same_org vers
 *   profiles_organisations, puis le profil.
 */
const ATTENDEE_SELECT =
  'id, reservation_id, organisation_id, profile_id, email, display_name, role, response, ' +
  'member:profiles_organisations!rra_member_same_org(photo_url, profile:profiles!profiles_organisations_profile_id_fkey(id, first_name, last_name, email))';

const RESERVATION_SELECT =
  '*, room:rooms!room_reservations_room_same_org(*), profile:profile_id(id, first_name, last_name, photo_url), ' +
  `attendees:room_reservation_attendees(${ATTENDEE_SELECT})`;

export const SLOT_TAKEN_MESSAGE = 'Ce créneau est déjà pris pour cette salle';
export const ROOM_INACTIVE_MESSAGE = 'Cette salle est désactivée : elle n\'est plus réservable.';

function toReservationError(error: PostgrestError): Error {
  if (error.code === '23P01') return new Error(SLOT_TAKEN_MESSAGE);
  if (error.code === '23514' && (error.hint === 'room_inactive' || error.message?.includes('désactivée'))) {
    return new Error(ROOM_INACTIVE_MESSAGE);
  }
  if (error.code === '23514' && error.message?.includes('video_link')) {
    return new Error('Le lien de visio doit commencer par http:// ou https://.');
  }
  return error as unknown as Error;
}

/** Champs écrits par le formulaire (title / description / visio inclus). */
export type ReservationWrite = Partial<Pick<RoomReservation,
  'room_id' | 'title' | 'starts_at' | 'ends_at' | 'description' | 'video_link' | 'add_video' | 'is_private' | 'status'
  // source : 'app' (défaut en base) ou 'voice' seulement — le reste est refusé par la base (lot 2).
  | 'source'>>;

/**
 * Toutes les bornes sont des INSTANTS ISO (timestamptz). Les appelants les
 * calculent dans le fuseau de la salle avec `utils/room-time.ts`.
 * Filtre par chevauchement réel : starts_at < fin ET ends_at > début.
 * Les réservations annulées (status = 'cancelled') ne sont jamais affichées.
 */
@Injectable({
  providedIn: 'root'
})
export class RoomReservationsService {
  private supabase = inject(SupabaseService);
  private auth = inject(AuthService);

  /** Réservations d'UNE salle chevauchant [startIso, endIso). */
  getReservationsByRoom(roomId: string, startIso: string, endIso: string): Observable<RoomReservation[]> {
    return from(
      this.supabase.client
        .from(Tables.RoomReservationsVisible)
        .select(RESERVATION_SELECT)
        .eq('room_id', roomId)
        .eq('organisation_id', this.auth.getOrganisationId())
        .eq('status', 'confirmed')
        .lt('starts_at', endIso)
        .gt('ends_at', startIso)
        .order('starts_at', { ascending: true })
    ).pipe(
      map(r => {
        if (r.error) throw r.error;
        return r.data as unknown as RoomReservation[];
      })
    );
  }

  /** Réservations de TOUTES les salles de l'organisation chevauchant [startIso, endIso). */
  getReservationsInRange(startIso: string, endIso: string): Observable<RoomReservation[]> {
    return from(
      this.supabase.client
        .from(Tables.RoomReservationsVisible)
        .select(RESERVATION_SELECT)
        .eq('organisation_id', this.auth.getOrganisationId())
        .eq('status', 'confirmed')
        .lt('starts_at', endIso)
        .gt('ends_at', startIso)
        .order('starts_at', { ascending: true })
    ).pipe(
      map(r => {
        if (r.error) throw r.error;
        return r.data as unknown as RoomReservation[];
      })
    );
  }

  /**
   * « Mes réunions » : réservations confirmées chevauchant [startIso, endIso)
   * dont je suis l'organisateur ou un participant membre.
   */
  getMyReservations(startIso: string, endIso: string): Observable<RoomReservation[]> {
    const me = this.auth.profile?.id;
    const organisationId = this.auth.getOrganisationId();
    return from(
      this.supabase.client
        .from(Tables.RoomReservationAttendees)
        .select('reservation_id')
        .eq('organisation_id', organisationId)
        .eq('profile_id', me)
    ).pipe(
      switchMap(({ data, error }) => {
        if (error) throw error;
        const ids = (data ?? []).map(a => a.reservation_id as string);
        const owner = `profile_id.eq.${me}`;
        const filter = ids.length ? `${owner},id.in.(${ids.join(',')})` : owner;
        return from(
          this.supabase.client
            .from(Tables.RoomReservationsVisible)
            .select(RESERVATION_SELECT)
            .eq('organisation_id', organisationId)
            .eq('status', 'confirmed')
            .or(filter)
            .lt('starts_at', endIso)
            .gt('ends_at', startIso)
            .order('starts_at', { ascending: true })
        );
      }),
      map(r => {
        if (r.error) throw r.error;
        return r.data as unknown as RoomReservation[];
      })
    );
  }

  /** Une réservation (vue masquée), participants compris. */
  getReservation(id: string): Observable<RoomReservation> {
    return from(
      this.supabase.client
        .from(Tables.RoomReservationsVisible)
        .select(RESERVATION_SELECT)
        .eq('id', id)
        .single()
    ).pipe(
      map(r => {
        if (r.error) throw r.error;
        return r.data as unknown as RoomReservation;
      })
    );
  }

  createReservation(data: ReservationWrite): Observable<RoomReservation> {
    return from(
      this.supabase.client
        .from(Tables.RoomReservations)
        .insert({
          ...data,
          profile_id: this.auth.profile?.id,
          organisation_id: this.auth.getOrganisationId()
        })
        // Colonnes masquées non lisibles sur la table : on relit par la vue.
        .select('id')
        .single()
    ).pipe(
      map(r => {
        if (r.error) throw toReservationError(r.error);
        return r.data.id as string;
      }),
      switchMap(id => this.getReservation(id))
    );
  }

  updateReservation(id: string, data: ReservationWrite): Observable<RoomReservation> {
    return from(
      this.supabase.client
        .from(Tables.RoomReservations)
        .update(data)
        .eq('id', id)
        .select('id')
        .single()
    ).pipe(
      map(r => {
        if (r.error) throw toReservationError(r.error);
        return r.data.id as string;
      }),
      switchMap(rid => this.getReservation(rid))
    );
  }

  /**
   * Annulation : la ligne reste (status = 'cancelled') pour envoyer le CANCEL
   * .ics aux participants et, au lot 3, propager l'annulation à Google /
   * Microsoft. Le créneau est libéré (contrainte limitée aux confirmées).
   */
  cancelReservation(id: string): Observable<RoomReservation> {
    return this.updateReservation(id, { status: 'cancelled' });
  }

  /** Suppression définitive (n'est plus utilisée par l'écran : préférer cancelReservation). */
  deleteReservation(id: string): Observable<void> {
    return from(
      this.supabase.client
        .from(Tables.RoomReservations)
        .delete()
        .eq('id', id)
        .eq('organisation_id', this.auth.getOrganisationId())
    ).pipe(
      map(r => {
        if (r.error) throw r.error;
        return;
      })
    );
  }

  /** Vrai si [startsAt, endsAt) (instants ISO) est libre dans la salle. */
  checkAvailability(
    roomId: string,
    startsAt: string,
    endsAt: string,
    excludeId?: string
  ): Observable<boolean> {
    let query = this.supabase.client
      .from(Tables.RoomReservations)
      .select('id')
      .eq('room_id', roomId)
      .eq('organisation_id', this.auth.getOrganisationId())
      .eq('status', 'confirmed')
      .lt('starts_at', endsAt)
      .gt('ends_at', startsAt);

    if (excludeId) {
      query = query.neq('id', excludeId);
    }

    return from(query).pipe(
      map(r => {
        if (r.error) throw r.error;
        return r.data.length === 0;
      })
    );
  }

  // ─── Participants ─────────────────────────────────────────────────────────

  /** Membres actifs de l'organisation active (sélecteur de participants). */
  getOrganisationMembers(): Observable<RoomMemberOption[]> {
    return from(
      // Ni retirés ni désactivés (MOKA-343), filtré par la base.
      currentMembersOnly(this.supabase.client
        .from(Tables.Profiles)
        .select(`id, first_name, last_name, email, ${PROFILE_MEMBERSHIP_SELECT_LIGHT}`)
        .eq('membership.organisation_id', this.auth.getOrganisationId()))
        .order('first_name')
        .order('last_name')
    ).pipe(
      map(({ data, error }) => {
        if (error) throw error;
        return flattenProfileMemberships(data as Partial<Profile>[])
          .filter(p => p.is_active !== false)
          .map(p => ({
            id: p.id as string,
            first_name: p.first_name ?? null,
            last_name: p.last_name ?? null,
            email: p.email ?? null,
            photo_url: p.photo_url ?? null,
          }));
      })
    );
  }

  async addAttendees(reservationId: string, drafts: AttendeeDraft[]): Promise<RoomReservationAttendee[]> {
    if (!drafts.length) return [];
    const organisationId = this.auth.getOrganisationId();
    const { data, error } = await this.supabase.client
      .from(Tables.RoomReservationAttendees)
      .insert(drafts.map(d => ({
        reservation_id: reservationId,
        organisation_id: organisationId,
        profile_id: d.profile_id,
        email: d.profile_id ? null : d.email?.trim(),
        display_name: d.profile_id ? null : (d.display_name?.trim() || null),
        role: d.role,
      })))
      .select(ATTENDEE_SELECT);
    if (error) throw error;
    return data as unknown as RoomReservationAttendee[];
  }

  async removeAttendees(ids: string[]): Promise<void> {
    if (!ids.length) return;
    const { error } = await this.supabase.client
      .from(Tables.RoomReservationAttendees)
      .delete()
      .in('id', ids);
    if (error) throw error;
  }

  async updateAttendeeRole(id: string, role: RoomAttendeeRole): Promise<void> {
    const { error } = await this.supabase.client
      .from(Tables.RoomReservationAttendees)
      .update({ role })
      .eq('id', id);
    if (error) throw error;
  }

  /** Réponse du participant membre connecté (seul autorisé par la base). */
  async respond(attendeeId: string, response: RoomAttendeeResponse): Promise<RoomReservationAttendee> {
    const { data, error } = await this.supabase.client
      .from(Tables.RoomReservationAttendees)
      .update({ response })
      .eq('id', attendeeId)
      .select(ATTENDEE_SELECT)
      .single();
    if (error) throw error;
    return data as unknown as RoomReservationAttendee;
  }
}
